// src/components/tourDesign.tsx
import { ReactNode } from 'react';
import tourMascot from '@/assets/tour.png';
import Joyride, { CallBackProps, Step } from 'react-joyride';

// Common Tour Content Component - Updated to match teacher/student design
export const TourContent = ({ title, content }: { title: string; content: string }): ReactNode => (
  <div className="tour-content flex items-start gap-6">
    <div className="flex-shrink-0">
      <img 
        src={tourMascot} 
        alt="Tour Guide" 
        className="w-20 h-20"
        style={{
          filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.1))'
        }}
      />
    </div>
    <div className="flex-1 text-left">
      <h2 className="text-xl font-semibold mb-2" style={{ color: '#525FE1' }}>{title}</h2>
      <p className="text-gray-600 text-base leading-relaxed">{content}</p>
    </div>
  </div>
);

// Common Styles Configuration - Updated to match teacher/student design
export const TOUR_STYLES = {
  options: {
    primaryColor: '#ffffff',
    zIndex: 10000,
    arrowColor: '#fff',
    backgroundColor: '#fff',
  },
  buttonNext: {
    backgroundColor: '#ffffff',
    color: '#525FE1',
    fontSize: '16px',
    padding: '10px 20px',
    borderRadius: '6px',
  },
  buttonBack: {
    color: '#666',
    marginRight: 12,
    fontSize: '16px',
    padding: '10px',
  },
  buttonSkip: {
    color: '#666',
    fontSize: '16px',
    padding: '10px',
  },
  tooltip: {
    padding: '24px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    color: '#333',
    fontSize: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    maxWidth: '500px',
    minWidth: '300px',
    pointerEvents: 'auto' as const,
    transition: 'opacity 0.2s ease',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  spotlight: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',

    backgroundColor: 'rgba(255, 255, 255, 0.3)', // White highlight
    // border: '2px solid white',
    // borderRadius: '8px',
    // boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)', 
  },
};

// Common Locale Configuration
export const TOUR_LOCALE = {
  back: 'Back',
  close: '',
  last: 'Done',
  next: 'Next',
  skip: 'Skip'
};

// Common Tour Props - Updated to match teacher/student design
export const DEFAULT_TOUR_PROPS = {
  continuous: true,
  hideCloseButton: true,
  hideBackButton: true,
  showProgress: true,
  showSkipButton: true,
  disableOverlayClose: true,
  disableCloseOnEsc: true,
  spotlightClicks: false,
  disableScrolling: false,
  scrollToFirstStep: true,
  scrollOffset: 100,
  floaterProps: {
    disableAnimation: true,
  },
  styles: TOUR_STYLES,
  locale: TOUR_LOCALE,
};

// Enhanced Overlay Component with proper z-index - Updated to match teacher/student design
export const TourOverlay = () => (
  <div 
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 1)', // Solid black, no transparency
      zIndex: 9999,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease',
    }}
  />
);

// Complete Tour Component with guaranteed dark background
export const StandardTour = ({
  steps,
  run,
  stepIndex,
  callback,
  joyrideRef
}: {
  steps: Step[];
  run: boolean;
  stepIndex: number;
  callback: (data: CallBackProps) => void;
  joyrideRef?: React.RefObject<any>;
}) => (
  <>
    <Joyride
      ref={joyrideRef}
      steps={steps}
      run={run}
      {...DEFAULT_TOUR_PROPS}
      stepIndex={stepIndex}
      callback={callback}
    />
    {run && <TourOverlay />}
  </>
);

// Helper function to create consistent steps - Updated to match teacher/student design
export const createTourStep = (
  target: string,
  title: string,
  content: string,
  placement: 'top' | 'right' | 'bottom' | 'left' | 'auto' = 'bottom',
  padding: number = 20
): Step => ({
  target,
  content: <TourContent title={title} content={content} />,
  disableBeacon: true,
  placement,
  spotlightPadding: padding,
  spotlightClicks: false,
  floaterProps: {
    disableAnimation: true
  }
});